import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dropdown, Input } from "../../common/components/Input";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import { useForm } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import ProfileInputStyle, {
  profileRowClassName,
} from "../../common/components/Input/ProfileInputStyle";
import type { RootState } from "../../common/state/store";
import type { ILanguage, IUser, IUserPreferences } from "../../model/model";
import {
  getCurrentOrganization,
  updateUser,
  getCurrentUser,
  setCurrentUserAndOrg,
  checkEmail,
} from "../../features/profile/state/profileSlice";
import type { IUserPreferencesPatch } from "../../features/profile/state/profileSlice";
import { enqueuePreferencesPatch } from "../../features/profile/state/preferencesWriter";
import { enqueueOnboardingEvent } from "../../features/onboarding/events/eventQueue";
import {
  resetOnboardingState,
  selectOnboardingRemote,
} from "../../features/onboarding/state/onboardingSlice";
import { IsLoading } from "../../common/components/AppState/IsLoading";
import { useMediaPicker } from "../../features/gallery/picker/useMediaPicker";
import ChangePasswordPopup from "../../common/components/Profile/ChangePasswordPopup";
import ChangeEmailPoppup from "../../common/components/Profile/ChangeMailConfirmPassword";
import MfaSection from "../../features/mfa/components/MfaSection";
import Avatar3dPopup from "../../common/components/Profile/Avatar3dPopup";
import AvatarPreview3d from "../../common/components/Profile/AvatarPreview3d";
import { checkMediaExists } from "../../features/gallery/state/gallerySlice";
import { useMediaById } from "../../features/gallery/useMediaById";
import {
  closeStandardModal,
  getCurrentModalCurrentState,
} from "../../common/state/slice/modal/modalSlice";
import RoundImagePreview from "../../features/projects/components/RoundImagePreview";
import Spinner from "../../common/components/Loader/Spinner";
import { setAlert } from "../../features/alert/state/alertsSlice";
import { useCallbackPrompt } from "../../common/util/useCallbackPrompt";
import ModalConfirmBeforeNavigatingOut from "../../common/components/Modal/ModalConfirmBeforeNavigatingOut";
import { getAnalyticsOptOut, getReleaseOptIn } from "./profileUtils";

const getAiProfile = (u?: IUser) => {
  try {
    const pref = u?.preferences;
    if (!pref) return { metier: "", targetAudience: "" };
    const obj: IUserPreferences =
      typeof pref === "string" ? (JSON.parse(pref) as IUserPreferences) : pref;
    return {
      metier: obj?.aiProfile?.metier || "",
      targetAudience: obj?.aiProfile?.targetAudience || "",
    };
  } catch {
    return { metier: "", targetAudience: "" };
  }
};
import InfoIconWithTooltip from "src/common/components/Icon/InfoIconWithTooltip";
const SHOW_ANALYTICS_OPT_OUT = false;

export const PROFILE_COMPONENT_ID = "profileComponent";
export const AVATAR3D_COMPONENT_ID = "avatar3dComponent";
export const CHANGEPASSWORD_COMPONENT_ID = "changePasswordComponent";
export const CHECK_EMAIL_COMPONENT_ID = "CkeckEmailComponent";
export const MFA_COMPONENT_ID = "mfaComponent";

export type ProfileValues = {
  firstname: string;
  lastname: string;
  email: string;
  source_avatar: number;
  avatar: string;
  preferred_language: number;
  wantToReceiveEmailAboutReleases: boolean;
  ai_metier: string;
  ai_targetAudience: string;
};

const languageDropdownOptions = [
  {
    disabled: false,
    value: "1",
    optionText: "Français",
  },
  {
    disabled: false,
    value: "2",
    optionText: "English",
  },
];

const ProfileMain = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const [isConfirmPasswordOpen, setIsConfirmPasswordOpen] = useState(true);

  const { currentUser, organization, onboardingRemote } = useAppSelector((state: RootState) => {
    return {
      onboardingRemote: selectOnboardingRemote(state),
      currentUser: getCurrentUser(state),
      organization: getCurrentOrganization(state),
    };
  });
  const [matchingAvatarId, setMatchingAvatarId] = useState<number | undefined>(undefined);
  const [lookedUpAvatarUrl, setLookedUpAvatarUrl] = useState<string | undefined>(undefined);
  useEffect(() => {
    const avatarUrl = currentUser?.avatar3d_url;
    if (!avatarUrl) {
      setMatchingAvatarId(undefined);
      setLookedUpAvatarUrl(undefined);
      return undefined;
    }
    let cancelled = false;
    dispatch(checkMediaExists({ s3Url: avatarUrl }))
      .unwrap()
      .then((res) => {
        if (cancelled) return;
        setMatchingAvatarId(res.id ?? undefined);
        setLookedUpAvatarUrl(avatarUrl);
      })
      .catch(() => {
        if (cancelled) return;
        setMatchingAvatarId(undefined);
        setLookedUpAvatarUrl(avatarUrl);
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch, currentUser?.avatar3d_url]);
  const { media: matchingAvatar, status: matchingAvatarStatus } = useMediaById(matchingAvatarId);
  const avatarThumbnailUrl = matchingAvatar?.thumbnail_s3_url;
  const isAvatarLookupPending =
    !!currentUser?.avatar3d_url && lookedUpAvatarUrl !== currentUser.avatar3d_url;
  const isAvatarResolving =
    isAvatarLookupPending ||
    (matchingAvatarId !== undefined && !matchingAvatar && matchingAvatarStatus === "loading");
  const avatar3dSignedUrl = matchingAvatar?.s3_url || currentUser?.avatar3d_url;
  const isEnduser = currentUser?.role?.name === "Enduser";
  const buttonResetDisabled =
    !onboardingRemote ||
    (Object.keys(onboardingRemote.tours).length === 0 &&
      Object.keys(onboardingRemote.checklist.items).length === 0 &&
      onboardingRemote.checklist.dismissedAt === null);

  const setFocusOnInput = (inputId: "firstname" | "lastname") => {
    // Wait for the next render cycle to set the focus
    setTimeout(() => {
      setFocus(inputId);
    }, 0);
  };

  useEffect(() => {
    if (!currentUser || !organization) {
      void dispatch(setCurrentUserAndOrg({ componentId: PROFILE_COMPONENT_ID })).then((action) => {
        if (!setCurrentUserAndOrg.fulfilled.match(action)) {
          return;
        }
        const fetchedUser = action.payload.currentUser;
        const aiP = getAiProfile(fetchedUser);
        reset({
          firstname: fetchedUser?.firstname,
          lastname: fetchedUser?.lastname,
          email: fetchedUser?.email,
          source_avatar: Number(fetchedUser?.source_avatar?.id),
          wantToReceiveEmailAboutReleases: getReleaseOptIn(fetchedUser),
          ai_metier: aiP.metier,
          ai_targetAudience: aiP.targetAudience,
        });
      });
    }
  }, []);

  const [disabledFirstName, setDisabledFirstName] = useState(true);
  const [disabledLastName, setDisabledLastName] = useState(true);
  const [disabledEmail, setDisabledEmail] = useState(true);
  const [disabledAiMetier, setDisabledAiMetier] = useState(true);
  const [disabledAiTargetAudience, setDisabledAiTargetAudience] = useState(true);
  const [disabledPassword, setDisabledPassword] = useState(true);
  const [disabledRole, setDisabledRole] = useState(true);
  const [umamiOptOut, setUmamiOptOut] = useState(
    () => localStorage.getItem("umami.disabled") === "1" || getAnalyticsOptOut(currentUser),
  );
  const initialUmamiOptOut = useRef(umamiOptOut);

  useEffect(() => {
    const accountOptOut = getAnalyticsOptOut(currentUser);
    if (accountOptOut && localStorage.getItem("umami.disabled") !== "1") {
      localStorage.setItem("umami.disabled", "1");
      setUmamiOptOut(true);
      initialUmamiOptOut.current = true;
    }
  }, [currentUser]);

  const modalState = useAppSelector(getCurrentModalCurrentState);
  const { pickMedia, pickerElement } = useMediaPicker();

  const {
    register,
    reset,
    handleSubmit,
    setValue,
    watch,
    getValues,
    setFocus,
    formState: { isDirty, errors: errorsProfile },
  } = useForm<ProfileValues>({
    defaultValues: {
      firstname: currentUser?.firstname,
      lastname: currentUser?.lastname,
      email: currentUser?.email,
      source_avatar: Number(currentUser?.source_avatar?.id),
      avatar: currentUser?.avatar3d_url,
      preferred_language: Number((currentUser?.preferred_language as ILanguage)?.id),
      wantToReceiveEmailAboutReleases: getReleaseOptIn(currentUser),
      ai_metier: getAiProfile(currentUser).metier,
      ai_targetAudience: getAiProfile(currentUser).targetAudience,
    },
  });
  useEffect(() => {
    if (modalState.showConfirmationModal.show === false) {
      setValue("email", currentUser?.email ? currentUser?.email : "");
    }
  }, [modalState]);
  const watchEmail = watch("email");

  const onSubmitProfile = (data: ProfileValues) => {
    void dispatch(
      checkEmail({
        componentId: CHECK_EMAIL_COMPONENT_ID,
        email: data.email,
        id: currentUser?.id,
      }),
    ).then((response) => {
      if (response.payload) {
        const preferencesPatch: IUserPreferencesPatch = {
          wantToReceiveEmailAboutReleases: !!data.wantToReceiveEmailAboutReleases,
          aiProfile: data.ai_metier?.trim()
            ? {
                metier: data.ai_metier.trim(),
                targetAudience: data.ai_targetAudience?.trim() || null,
              }
            : null,
        };

        const updatedUser = {
          id: Number(currentUser?.id),
          firstname: data.firstname,
          lastname: data.lastname,
          email: data.email?.toLocaleLowerCase(),
          preferred_language: data.preferred_language,
          source_avatar: data.source_avatar,
        };

        setIsConfirmPasswordOpen(true);
        dispatch(closeStandardModal("modalConfirmPasswordForEmail"));

        Promise.all([
          dispatch(updateUser({ componentId: PROFILE_COMPONENT_ID, ...updatedUser })),
          enqueuePreferencesPatch(dispatch, preferencesPatch),
        ])
          .then(([, preferencesSaved]) => {
            if (!preferencesSaved) {
              throw new Error("Failed to save preferences");
            }
            reset({
              firstname: data.firstname,
              lastname: data.lastname,
              email: data.email,
              source_avatar: data.source_avatar,
              preferred_language: data.preferred_language,
              wantToReceiveEmailAboutReleases: data.wantToReceiveEmailAboutReleases,
              ai_metier: data.ai_metier,
              ai_targetAudience: data.ai_targetAudience,
            });

            setDisabledFirstName(true);
            setDisabledLastName(true);
            setDisabledEmail(true);
            setDisabledPassword(true);
            setDisabledAiMetier(true);
            setDisabledAiTargetAudience(true);
            initialUmamiOptOut.current = umamiOptOut;

            setTimeout(() => {
              dispatch(setAlert({ type: "success", msg: t("alerts.profileEditSuccess") }));
            }, 1);
          })
          .catch((e: unknown) => {
            console.error("catch: ", e);
          });
      } else {
        reset({
          firstname: currentUser?.firstname,
          lastname: currentUser?.lastname,
          email: currentUser?.email,
          source_avatar: Number(currentUser?.source_avatar?.id),
          preferred_language: Number((currentUser?.preferred_language as ILanguage)?.id),
          wantToReceiveEmailAboutReleases: getReleaseOptIn(currentUser),
        });
        setDisabledFirstName(true);
        setDisabledLastName(true);
        setDisabledEmail(true);
        setDisabledPassword(true);
        setDisabledAiMetier(true);
        setDisabledAiTargetAudience(true);
        dispatch(
          setAlert({
            type: "danger",
            msg: t("alerts.emailCheck"),
          }),
        );
      }
    });
  };

  const cancel = () => {
    reset({
      firstname: currentUser?.firstname,
      lastname: currentUser?.lastname,
      email: currentUser?.email,
      source_avatar: Number(currentUser?.source_avatar?.id),
      preferred_language: Number((currentUser?.preferred_language as ILanguage)?.id),
      wantToReceiveEmailAboutReleases: getReleaseOptIn(currentUser),
      ai_metier: getAiProfile(currentUser).metier,
      ai_targetAudience: getAiProfile(currentUser).targetAudience,
    });
    setDisabledFirstName(true);
    setDisabledLastName(true);
    setDisabledEmail(true);
    setDisabledPassword(true);
    setDisabledAiMetier(true);
    setDisabledAiTargetAudience(true);
  };

  const handleAvatar3dChange = (url: string) => {
    void dispatch(
      updateUser({
        componentId: PROFILE_COMPONENT_ID,
        id: Number(currentUser?.id),
        avatar3d_url: url,
      }),
    );
  };

  const userIsAdminOfOrg = Number(currentUser?.id) === Number(organization?.admin.id);
  const pickPhoto = async () => {
    const media = await pickMedia({
      allowedMediaTypes: "image",
      initialSearch: { mediaType: "image", sort: "updated_at_desc", media360tag: "" },
      initialMediaId: Number(getValues("source_avatar")) || 0,
    });
    if (media) setValue("source_avatar", Number(media.id));
  };

  const hasAnythingChanged =
    Number(currentUser?.source_avatar?.id) !== watch("source_avatar") ||
    isDirty ||
    umamiOptOut !== initialUmamiOptOut.current;

  const getRoleName = (currentUser?: IUser) => {
    if (userIsAdminOfOrg) {
      return t("general.roles.admin");
    } else if (Number(currentUser?.role.id) === 5) {
      return t("general.roles.creator");
    } else {
      return t("general.roles.enduser");
    }
  };

  const hasSomethingChanged = () => {
    if (currentUser?.firstname !== getValues("firstname")) {
      return true;
    }

    if (currentUser?.lastname !== getValues("lastname")) {
      return true;
    }

    if (currentUser?.email !== getValues("email")) {
      return true;
    }

    if (Number(currentUser?.source_avatar.id) !== getValues("source_avatar")) {
      return true;
    }

    if (Number(currentUser?.preferred_language?.id) !== Number(getValues("preferred_language"))) {
      return true;
    }
    if (getReleaseOptIn(currentUser) !== !!getValues("wantToReceiveEmailAboutReleases")) {
      return true;
    }
    const savedAi = getAiProfile(currentUser);
    if (savedAi.metier !== (getValues("ai_metier") || "")) {
      return true;
    }
    if (savedAi.targetAudience !== (getValues("ai_targetAudience") || "")) {
      return true;
    }
    return false;
  };

  const [showPrompt, confirmNavigation, cancelNavigation] = useCallbackPrompt(
    hasSomethingChanged(),
  );
  const resetTutorial = () => {
    void dispatch(resetOnboardingState()).then((action) => {
      if (resetOnboardingState.fulfilled.match(action)) {
        dispatch(
          setAlert({
            type: "success",
            msg: t("alerts.tutorialResetSuccess"),
          }),
        );
        enqueueOnboardingEvent({
          event: "reset_all",
          surface: "profile",
          itemId: null,
          step: null,
        });
      } else {
        dispatch(
          setAlert({
            type: "danger",
            msg: t("alerts.tutorialResetFail"),
          }),
        );
      }
    });
  };

  return (
    <>
      <ChangePasswordPopup email={currentUser?.email ?? ""} />
      <Avatar3dPopup
        currentUserAvatar3dUrl={avatar3dSignedUrl ?? ""}
        handleAvatar3dChange={handleAvatar3dChange}
      />
      {pickerElement}
      <div className="relative grow w-full pl-4 pr-4 px-4 mx-auto personalize-scroll-visible overflow-y-auto">
        <h2 className="text-2xl font-medium mb-2 mt-2 xl:mt-4">
          {t("pages.profile.tabs.myDetails")}
        </h2>
        <h3 className="py-3">{t("pages.profile.main.subheading")}</h3>
        <div className="completely-centeredProfile">
          <IsLoading componentId={PROFILE_COMPONENT_ID} showSuccess={false} />
        </div>
        <div className="mt-2 border-gray-300">
          <form className="h-full" onSubmit={handleSubmit(onSubmitProfile)}>
            <ProfileInputStyle
              title={t("general.firstname")}
              disabled={disabledFirstName}
              setDisabled={setDisabledFirstName}
              setFocus={() => setFocusOnInput("firstname")}
            >
              <Input
                type="text"
                highlight={disabledFirstName}
                readOnly={disabledFirstName}
                registerFct={() => register("firstname")}
                className="font-medium bg-white read-only:bg-slate-100 read-only:border-transparent read-only:shadow-none read-only:focus:ring-0 text-gray-600 max-w-max text-center sm:text-left border-0 ${ ? }"
                placeholder={t("pages.profile.main.firstNamePlaceholder")}
              />
            </ProfileInputStyle>
            <ProfileInputStyle
              title={t("general.lastname")}
              disabled={disabledLastName}
              setDisabled={setDisabledLastName}
              setFocus={() => setFocusOnInput("lastname")}
            >
              <Input
                type="text"
                highlight={disabledLastName}
                readOnly={disabledLastName}
                registerFct={() => register("lastname")}
                className="font-medium bg-white read-only:bg-slate-100 read-only:border-transparent read-only:shadow-none read-only:focus:ring-0 text-gray-600  max-w-max text-center sm:text-left border-0 ${ ? }"
                placeholder={t("pages.profile.main.lastNamePlaceholder")}
              />
            </ProfileInputStyle>
            <ProfileInputStyle
              title={t("general.email")}
              disabled={disabledEmail}
              setDisabled={setDisabledEmail}
            >
              <Input
                type="email"
                readOnly={disabledEmail}
                registerFct={() => register("email", { required: true, pattern: /^\S+@\S+$/i })}
                className="mr-2 font-medium bg-white read-only:bg-slate-100 read-only:border-transparent read-only:shadow-none read-only:focus:ring-0 text-gray-600 text-center max-w-max sm:text-left border-0 ${ ? }"
                placeholder={t("pages.profile.main.emailPlaceholder")}
              />
              <ChangeEmailPoppup
                setIsConfirmPasswordOpen={setIsConfirmPasswordOpen}
                register={register}
                onsubmit={handleSubmit(onSubmitProfile)}
                isConfirmPasswordOpen={isConfirmPasswordOpen}
                email={watchEmail}
              />
              <ErrorMessage
                errors={errorsProfile}
                name="email"
                render={() => (
                  <div className="font-small text-red-700">{"Ne peux pas être vide!"}</div>
                )}
              />
            </ProfileInputStyle>
            <ProfileInputStyle
              title={t("pages.profile.main.photo")}
              setValueFct={setValue}
              onPickImage={() => void pickPhoto()}
            >
              <RoundImagePreview
                componentId={PROFILE_COMPONENT_ID}
                mediaId_OrIsItAnObjectLol={watch("source_avatar")}
                alt="Logo"
                bigger
              />
            </ProfileInputStyle>
            <ProfileInputStyle
              title={t("general.avatar")}
              avatarStatus={currentUser?.avatar3d_url ? "ready" : "loading"}
            >
              {avatarThumbnailUrl ? (
                <img
                  className="inline object-cover w-16 h-16 mr-10 sm:ml-4 mr-2 rounded-full border border-gray-300"
                  src={avatarThumbnailUrl}
                  alt="Avatar"
                />
              ) : isAvatarResolving ? (
                <Spinner relative />
              ) : avatar3dSignedUrl ? (
                <AvatarPreview3d
                  url={avatar3dSignedUrl}
                  className="w-16 h-16 mr-10 sm:ml-4 mr-2 rounded-full border border-gray-300 overflow-hidden"
                  canvasClassName="w-[130%] h-[130%] -ml-[24%] -mt-[21%]"
                />
              ) : !currentUser ? (
                <Spinner relative />
              ) : (
                <p className="text-sm text-gray-400 italic ml-4">
                  {t("pages.profile.main.noAvatarCta")}
                </p>
              )}
            </ProfileInputStyle>

            <div className={profileRowClassName}>
              <p className="w-full sm:w-1/3 text-sm text-center sm:text-left">
                {t("general.language")}
              </p>

              <Dropdown
                options={languageDropdownOptions}
                asDefaultValue
                languague
                defaultValueIndex={languageDropdownOptions.findIndex((v) => {
                  const userPrefLanguage = currentUser?.preferred_language as ILanguage;
                  return Number(userPrefLanguage?.id) === Number(v.value);
                })}
                registerFct={() => register("preferred_language")}
              />
            </div>

            {!isEnduser && (
              <div className={profileRowClassName}>
                <div className="w-full sm:w-1/3 flex items-center justify-center sm:justify-start text-sm text-center sm:text-left">
                  <p>{t("pages.profile.main.wantToReceiveEmailAboutReleases")}</p>
                  <InfoIconWithTooltip
                    message={t("tooltip.wantToReceiveEmailAboutReleases")}
                    className="ml-2"
                  />
                </div>

                <input
                  type="checkbox"
                  className="rounded border-gray-300 text-green-600 shadow-sm focus:ring-green-500"
                  {...register("wantToReceiveEmailAboutReleases")}
                />
              </div>
            )}

            {SHOW_ANALYTICS_OPT_OUT && (
              <div className={profileRowClassName}>
                <div className="w-full sm:w-1/3 flex items-center justify-center sm:justify-start text-sm text-center sm:text-left">
                  <p>{t("pages.profile.main.umamiOptOut")}</p>
                  <InfoIconWithTooltip message={t("tooltip.umamiOptOut")} className="ml-2" />
                </div>

                <input
                  type="checkbox"
                  className="rounded border-gray-300 text-green-600 shadow-sm focus:ring-green-500"
                  checked={umamiOptOut}
                  onChange={(e) => {
                    const optedOut = e.target.checked;
                    setUmamiOptOut(optedOut);
                    if (optedOut) {
                      localStorage.setItem("umami.disabled", "1");
                    } else {
                      localStorage.removeItem("umami.disabled");
                    }
                    void enqueuePreferencesPatch(dispatch, { analytics_opt_out: optedOut });
                  }}
                />
              </div>
            )}

            {!isEnduser && (
              <>
                <h2 className="text-2xl font-medium mb-2 mt-8">
                  {t("pages.profile.main.aiProfileTitle")}
                </h2>
                <h3 className="py-3">{t("pages.profile.main.aiProfileDescription")}</h3>

                <ProfileInputStyle
                  title={t("pages.profile.main.aiMetier")}
                  disabled={disabledAiMetier}
                  setDisabled={setDisabledAiMetier}
                >
                  <Input
                    type="text"
                    highlight={disabledAiMetier}
                    readOnly={disabledAiMetier}
                    registerFct={() => register("ai_metier")}
                    className="font-medium bg-white read-only:bg-slate-100 read-only:border-transparent read-only:shadow-none read-only:focus:ring-0 text-gray-600 flex-1 text-center sm:text-left border-0"
                    placeholder={t("pages.profile.main.aiMetierPlaceholder")}
                  />
                </ProfileInputStyle>

                <ProfileInputStyle
                  title={t("pages.profile.main.aiTargetAudience")}
                  disabled={disabledAiTargetAudience}
                  setDisabled={setDisabledAiTargetAudience}
                >
                  <Input
                    type="text"
                    highlight={disabledAiTargetAudience}
                    readOnly={disabledAiTargetAudience}
                    registerFct={() => register("ai_targetAudience")}
                    className="font-medium bg-white read-only:bg-slate-100 read-only:border-transparent read-only:shadow-none read-only:focus:ring-0 text-gray-600 flex-1 text-center sm:text-left border-0"
                    placeholder={t("pages.profile.main.aiTargetAudiencePlaceholder")}
                  />
                </ProfileInputStyle>
              </>
            )}

            <div className="flex justify-end">
              <button
                type="button"
                className="btn-alternative-outline mr-4"
                onClick={() => {
                  cancel();
                }}
              >
                {t("general.cancel")}
              </button>
              <button className="btn-primary-fill" type="submit" disabled={!hasAnythingChanged}>
                {t("general.save")}
              </button>
            </div>
          </form>

          <h2 className="text-2xl font-medium mb-3 mt-8">{t("pages.profile.tabs.myAccount")}</h2>
          <ProfileInputStyle
            title={t("general.password")}
            disabled={disabledPassword}
            setDisabled={setDisabledPassword}
          >
            <Input
              type="text"
              readOnly={disabledPassword}
              className=" mr-2 font-medium bg-white read-only:bg-slate-100 read-only:border-transparent read-only:shadow-none read-only:focus:ring-0 read-only:placeholder-gray-600  max-w-max text-center sm:text-left border-0 ${ ? }"
              placeholder={"*********"}
            />
          </ProfileInputStyle>

          <ProfileInputStyle
            title={t("general.roles.role")}
            disabled={disabledRole}
            setDisabled={setDisabledRole}
          >
            <Input
              type="text"
              readOnly={disabledRole}
              className="font-medium bg-white read-only:bg-slate-100 read-only:border-transparent read-only:shadow-none read-only:focus:ring-0 text-gray-600 max-w-max text-center sm:text-left border-0 ${ ? }"
              value={getRoleName(currentUser)}
            />
          </ProfileInputStyle>
          <div className="flex justify-end mb-4 mr-1">
            <button
              className="btn-alternative-outline"
              onClick={() => resetTutorial()}
              disabled={buttonResetDisabled}
            >
              {t("pages.profile.tabs.resetTutorial")}
            </button>
          </div>

          <MfaSection componentId={MFA_COMPONENT_ID} />
        </div>
      </div>
      <ModalConfirmBeforeNavigatingOut
        showDialog={showPrompt}
        cancelNavigation={cancelNavigation}
        confirmNavigation={confirmNavigation}
      />
    </>
  );
};

export default ProfileMain;
